import { createDevLogger } from '@terragotech/gen5-shared-utilities';
import { CallbackInterface, MutableSnapshot, RecoilState, RecoilValue, SerializableParam } from 'recoil';

const devLog = createDevLogger(`Recoil: `);

type RecoilFamily<TValue, TParam> = (param: TParam) => RecoilValue<TValue>;

export interface InitializeRecoilValueOptions<TParam extends SerializableParam = SerializableParam> {
  param?: TParam;
  verbose?: boolean;
}
const defaultOptions: InitializeRecoilValueOptions = {
  verbose: false,
} as const;

export const initializeRecoilValue = <TValue>(
  state: RecoilValue<TValue>,
  { snapshot, set, refresh }: CallbackInterface,
  options: InitializeRecoilValueOptions = defaultOptions
): Promise<TValue> => {
  options.verbose && devLog(`Initialization (manual) enabled for key '${state.key}'.`, state);
  try {
    set(state as RecoilState<TValue>, prev => prev);
  } catch (e) {
    refresh(state); // Read-only selectors will throw on `set`.
  }
  return snapshot.getPromise(state);
};

export const initializeRecoilValueCallback =
  (cbInterface: CallbackInterface) =>
  async <TValue>(state: RecoilValue<TValue>, options: InitializeRecoilValueOptions = defaultOptions) =>
    await initializeRecoilValue<TValue>(state, cbInterface, options);

export const initializeRecoilFamilyCallback =
  (cbInterface: CallbackInterface) =>
  async <TValue, TParam extends SerializableParam>(
    familyState: RecoilFamily<TValue, TParam>,
    params: TParam[],
    options: InitializeRecoilValueOptions = defaultOptions
  ) =>
    await Promise.all(params.map(p => initializeRecoilValue<TValue>(familyState(p), cbInterface, options)));

export const getInitializeRecoilValueCallback =
  <TValue>(state: RecoilValue<TValue>, options: InitializeRecoilValueOptions = defaultOptions) =>
  (cbInterface: CallbackInterface) =>
  async () =>
    await initializeRecoilValue<TValue>(state, cbInterface, options);

export const getInitializeRecoilValueFamilyCallback =
  <TValue, TParam extends SerializableParam>(
    familyState: RecoilFamily<TValue, TParam>,
    params: TParam[],
    options: InitializeRecoilValueOptions = defaultOptions
  ) =>
  (cbInterface: CallbackInterface) =>
  async () =>
    await Promise.all(params.map(p => initializeRecoilValue<TValue>(familyState(p), cbInterface, options)));

/**
 * An optional function that takes a {@link MutableSnapshot} to initialize the `<RecoilRoot>` atom state. This sets up the state for the
 * initial render and is not intended for subsequent state changes or async initialization. Atom effect initializations take precedence
 * over `initializeState`.
 * @param mutableSnapshot The mutable snapshot to be initialized.
 * @param options Initialization options.
 * @see `mutableSnapshot` method {@link mutableSnapshot `getNodes_UNSTABLE`} includes Atoms and Selectors, but excludes Families (which are technically methods which return new nodes).
 * @throws `mutableSnapshot` methods {@link mutableSnapshot `getID`, `isRetained`, and `retain`} appear unimplemented for initialization, and will throw if invoked.
 * @see https://recoiljs.org/docs/api-reference/core/RecoilRoot
 * @see https://recoiljs.org/docs/api-reference/core/Snapshot#state-initialization
 */
export const initializeRecoilRoot = (
  mutableSnapshot: MutableSnapshot,
  options: InitializeRecoilValueOptions = defaultOptions
): void => {
  options.verbose && devLog(`Root initialization started...`);
  // TODO: [S5-3741] Do we want to persist all recoil values as a whole (e.g. as a `Snapshot`), and load as `initialValue`?
  options.verbose && devLog(`Root initialization complete.`);
};
